import { useTranslation } from "react-i18next";
import { StyledTabs } from "components/StyledTabs";
import { GRANULARITIES, type Granularity } from "components/charts/granularity";

export interface GranularityToggleProps {
  value: Granularity;
  /** Granularities valid for the selected window, as returned by the backend. */
  availableGranularities: Granularity[];
  onChange: (granularity: Granularity) => void;
  className?: string;
}

const labelKeys: Record<Granularity, string> = {
  daily: "components:granularityToggle.daily",
  weekly: "components:granularityToggle.weekly",
  monthly: "components:granularityToggle.monthly",
};

// Each disabled option explains its own range limit rather than a generic note.
const unavailableKeys: Record<Granularity, string> = {
  daily: "components:granularityToggle.unavailableDaily",
  weekly: "components:granularityToggle.unavailableWeekly",
  monthly: "components:granularityToggle.unavailableMonthly",
};

/**
 * Daily / Weekly / Monthly segmented control on the `StyledTabs` pill variant.
 * Options that don't fit the selected window are disabled and faded;
 * `pointer-events` stays on so their tooltip can show (a disabled button
 * ignores clicks regardless).
 */
export const GranularityToggle = ({
  value,
  availableGranularities,
  onChange,
  className,
}: GranularityToggleProps) => {
  const { t } = useTranslation(["components"]);

  return (
    <StyledTabs<Granularity>
      activeTab={value}
      onTabChange={onChange}
      variant="pill"
      className={className}
      listClassName="space-x-0.5"
      triggerClassName="px-3 text-xs disabled:pointer-events-auto disabled:opacity-40"
      tabs={GRANULARITIES.map((granularity) => {
        const enabled = availableGranularities.includes(granularity);
        return {
          value: granularity,
          label: t(labelKeys[granularity]),
          disabled: !enabled,
          title: enabled ? undefined : t(unavailableKeys[granularity]),
        };
      })}
    />
  );
};
